import type { ExploreFormat, ExploreNode } from '@/types/api'
import { formatPath, isAddressableKey, isKeyableValue, parsePath, valueAt, KEY_FIELD_RE, type PathSegment } from './json-path'

/**
 * Data Explorer model: nodes come from `GET /data-sources/{id}/explore` (or, for unsaved test
 * results, from `exploreValue` below, which mirrors the server). Arrays of objects with a
 * unique identifier are "keyed": items get stable paths like `menu.products[?id=123]`.
 */

/* ------------------------------------------------------------ presentation */

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

/** "products" → "Product", "categories" → "Category" (fallback "Item"). */
export function singular(name: string | null | undefined): string {
  if (!name || !/^[A-Za-z][A-Za-z_ ]*$/.test(name)) return 'Item'
  const n = name.replace(/_/g, ' ').trim()
  if (/ies$/i.test(n)) return cap(n.slice(0, -3) + 'y')
  if (/(ss|us|news|series)$/i.test(n)) return cap(n)
  if (/s$/i.test(n)) return cap(n.slice(0, -1))
  return cap(n)
}

/**
 * Display title. Keyed items: "Green tea (id 123)" or "Product 123"; positional items:
 * "Green tea (#1)" or "Product #1"; other nodes: their name.
 */
export function nodeTitle(node: ExploreNode, parent?: Pick<ExploreNode, 'name' | 'key_field'> | null, parentIsRoot = false): string {
  const noun = parentIsRoot ? 'Item' : singular(parent?.name)
  if (node.key !== undefined) {
    const field = parent?.key_field ?? null
    if (node.label) return field ? `${node.label} (${field} ${node.key})` : `${node.label} (${node.key})`
    return `${noun} ${node.key}`
  }
  if (node.index !== undefined) return node.label ? `${node.label} (#${node.index + 1})` : `${noun} #${node.index + 1}`
  return node.name
}

const FORMAT_LABELS: Record<ExploreFormat, string> = { image: 'Image', url: 'Link', datetime: 'Date & time', date: 'Date', numeric: 'Number (as text)' }

/** "Text", "Whole number", "List · 12 items"… */
export function typeLabel(node: Pick<ExploreNode, 'type' | 'format' | 'count'>): string {
  switch (node.type) {
    case 'string':
      return node.format ? FORMAT_LABELS[node.format] : 'Text'
    case 'integer':
      return 'Whole number'
    case 'number':
      return 'Number'
    case 'boolean':
      return 'Yes / No'
    case 'null':
      return 'Empty'
    case 'array':
      return `List · ${node.count ?? 0} item${node.count === 1 ? '' : 's'}`
    case 'object':
      return `Group · ${node.count ?? 0} field${node.count === 1 ? '' : 's'}`
  }
}

/** One-line sample for scalar nodes ('' for containers). */
export function sampleText(node: Pick<ExploreNode, 'type' | 'sample'>, max = 80): string {
  if (node.type === 'object' || node.type === 'array') return ''
  if (node.type === 'null' || node.sample === null || node.sample === undefined) return node.type === 'null' ? 'empty' : ''
  if (typeof node.sample === 'boolean') return node.sample ? 'yes' : 'no'
  const s = String(node.sample)
  if (s.startsWith('data:')) return `${s.slice(0, 22)}…`
  return s.length > max ? `${s.slice(0, max - 1)}…` : s
}

export function isContainer(node: Pick<ExploreNode, 'type'>): boolean {
  return node.type === 'object' || node.type === 'array'
}

/** Scalars with a path can be bound (`{{ path }}`). */
export function isBindableNode(node: Pick<ExploreNode, 'type' | 'path' | 'unbindable'>): boolean {
  return !!node.path && !node.unbindable && !isContainer(node) && node.type !== 'null'
}

export function bindingText(path: string): string {
  return `{{ ${path} }}`
}

/* ----------------------------------------------------------------- tree state */

/** Loaded children per container node id (= its path). */
export interface ChildPage {
  children: ExploreNode[]
  count: number
  truncated: boolean
  /** Offset of the next page. */
  nextOffset: number
}
export type ExploreTree = Readonly<Record<string, ChildPage>>

/** Stable id: the path, or parent id + name for unbindable nodes. */
export function nodeId(node: Pick<ExploreNode, 'path' | 'name'>, parentId: string): string {
  return node.path ?? `${parentId}›${node.name}`
}

/**
 * Stores the children a response carried (recursively, for `depth` > 1). `append` adds a
 * "Load more" page after the loaded ones.
 */
export function absorbNode(tree: ExploreTree, node: ExploreNode, append = false, id: string = node.path ?? node.name): ExploreTree {
  if (!node.children) return tree
  const prev = tree[id]
  const children = append && prev ? [...prev.children, ...node.children.filter((c) => !prev.children.some((p) => p.path !== null && p.path === c.path))] : node.children
  let next: Record<string, ChildPage> = { ...tree, [id]: { children, count: node.count ?? children.length, truncated: !!node.truncated, nextOffset: (node.offset ?? 0) + node.children.length } }
  for (const c of node.children) if (c.children) next = absorbNode(next, c, false, nodeId(c, id)) as Record<string, ChildPage>
  return next
}

/** Drops loaded pages below `id` (e.g. when the item identifier changes). */
export function forgetBelow(tree: ExploreTree, id: string): ExploreTree {
  return Object.fromEntries(Object.entries(tree).filter(([k]) => k !== id && !k.startsWith(`${id}.`) && !k.startsWith(`${id}[`) && !k.startsWith(`${id}›`)))
}

function matches(node: ExploreNode, q: string): boolean {
  const hay = [node.name, node.label, node.key, typeof node.sample === 'string' || typeof node.sample === 'number' ? String(node.sample) : null]
  return hay.some((h) => !!h && h.toLowerCase().includes(q))
}

/**
 * Client-side search over loaded nodes: `visible` = ids that match or have a matching
 * descendant; `expand` = ids to open so matches show. Null when the query is empty.
 */
export function filterTree(root: ExploreNode, tree: ExploreTree, query: string): { visible: Set<string>; expand: Set<string>; matches: number } | null {
  const q = query.trim().toLowerCase()
  if (!q) return null
  const visible = new Set<string>()
  const expand = new Set<string>()
  let count = 0
  const walk = (node: ExploreNode, id: string, seen: Set<string>): boolean => {
    if (seen.has(id)) return false
    seen.add(id)
    let hit = matches(node, q)
    if (hit) count++
    const page = tree[id]
    let below = false
    for (const c of page?.children ?? []) if (walk(c, nodeId(c, id), seen)) below = true
    if (below) expand.add(id)
    hit = hit || below
    if (hit) visible.add(id)
    return hit
  }
  walk(root, root.path ?? root.name, new Set())
  return { visible, expand, matches: count }
}

/* ------------------------------------------------- local exploration (tests) */

const ID_FIELDS = ['id', 'sku', 'code', 'key', 'slug', 'uuid', 'ean', 'gtin', 'barcode', 'number']
const LABEL_FIELDS = ['name', 'title', 'label']

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

export function detectFormat(s: string): ExploreFormat | null {
  if (/^data:image\//.test(s) || /^https?:\/\/\S+\.(?:png|jpe?g|gif|webp|svg|avif)(?:\?\S*)?$/i.test(s)) return 'image'
  if (/^https?:\/\/\S+$/.test(s)) return 'url'
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return 'datetime'
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'date'
  if (/^-?\d+(?:\.\d+)?$/.test(s.trim())) return 'numeric'
  return null
}

const idRank = (f: string) => {
  const i = ID_FIELDS.indexOf(f.toLowerCase())
  return i >= 0 ? i : f.toLowerCase().endsWith('_id') ? ID_FIELDS.length : Infinity
}

/** Fields that identify every item of an array of objects uniquely (id-like first). */
export function keyCandidates(arr: readonly unknown[]): string[] {
  if (!arr.length || !arr.every(isObj)) return []
  const fields = Object.keys(arr[0] as object).filter((f) => KEY_FIELD_RE.test(f))
  const ok = fields.filter((f) => {
    const seen = new Set<string>()
    for (const item of arr as Record<string, unknown>[]) {
      const v = item[f]
      if (!isKeyableValue(v)) return false
      const k = String(v)
      if (seen.has(k)) return false
      seen.add(k)
    }
    return true
  })
  return ok.sort((a, b) => idRank(a) - idRank(b))
}

/** Auto-detected identifier: the best id-like candidate (null = positional). */
export function autoKeyField(candidates: readonly string[]): string | null {
  return candidates.find((f) => Number.isFinite(idRank(f))) ?? null
}

function labelOf(item: Record<string, unknown>): string | null {
  for (const f of LABEL_FIELDS) if (typeof item[f] === 'string' && item[f]) return (item[f] as string).slice(0, 120)
  return null
}

export interface ExploreOptions {
  /** For the array at the path: a field, `none` (positional) or undefined (auto). */
  keyField?: string | null
  offset?: number
  limit?: number
  depth?: number
}

function typeOf(v: unknown): ExploreNode['type'] {
  if (v === null || v === undefined) return 'null'
  if (Array.isArray(v)) return 'array'
  if (typeof v === 'object') return 'object'
  if (typeof v === 'string') return 'string'
  if (typeof v === 'number') return Number.isInteger(v) ? 'integer' : 'number'
  if (typeof v === 'boolean') return 'boolean'
  return 'null'
}

function build(value: unknown, name: string, segments: PathSegment[] | null, alias: string, depth: number, opts: ExploreOptions, top: boolean, extra: Partial<ExploreNode> = {}): ExploreNode {
  const type = typeOf(value)
  const node: ExploreNode = { name, path: segments ? formatPath(alias, segments) : null, type, ...extra }
  if (!segments) node.unbindable = true
  if (type === 'string') {
    const s = value as string
    node.sample = s.length > 200 ? s.slice(0, 200) : s
    node.format = detectFormat(s)
    return node
  }
  if (type === 'integer' || type === 'number' || type === 'boolean') {
    node.sample = value as number | boolean
    return node
  }
  if (type === 'null') {
    node.sample = null
    return node
  }
  const offset = top ? Math.max(0, opts.offset ?? 0) : 0
  const limit = Math.min(200, Math.max(1, opts.limit ?? 50))
  if (type === 'object') {
    const keys = Object.keys(value as object)
    node.count = keys.length
    if (depth > 0) {
      const slice = keys.slice(offset, offset + limit)
      node.children = slice.map((k) => build((value as Record<string, unknown>)[k], k, segments && isAddressableKey(k) ? [...segments, k] : null, alias, depth - 1, opts, false))
      node.offset = offset
      node.limit = limit
      node.truncated = offset + slice.length < keys.length
    }
    return node
  }
  const arr = value as unknown[]
  node.count = arr.length
  const types = new Set(arr.map(typeOf))
  node.item_type = types.size === 1 ? [...types][0]! : types.size === 0 ? 'null' : 'mixed'
  let keyField: string | null = null
  if (node.item_type === 'object') {
    const candidates = keyCandidates(arr)
    node.key_candidates = candidates
    const wanted = top ? opts.keyField : undefined
    if (wanted === 'none') keyField = null
    else if (wanted) {
      if (candidates.includes(wanted)) keyField = wanted
      else node.key_error = `“${wanted}” doesn’t identify every item uniquely.`
    } else keyField = autoKeyField(candidates)
    node.key_field = keyField
    node.keyed = !!keyField
  }
  if (depth > 0) {
    const slice = arr.slice(offset, offset + limit)
    node.children = slice.map((item, j) => {
      const index = offset + j
      if (keyField && isObj(item)) {
        const key = String(item[keyField])
        const label = labelOf(item)
        return build(item, label ?? `${keyField} ${key}`, segments ? [...segments, { field: keyField, value: key }] : null, alias, depth - 1, opts, false, { key, label, index })
      }
      return build(item, `[${index}]`, segments ? [...segments, index] : null, alias, depth - 1, opts, false, { index, ...(isObj(item) ? { label: labelOf(item) } : {}) })
    })
    node.offset = offset
    node.limit = limit
    node.truncated = offset + slice.length < arr.length
  }
  return node
}

/** Explores a local value like the server's explore endpoint. */
export function exploreValue(root: unknown, alias: string, path: string = alias, opts: ExploreOptions = {}): { node: ExploreNode } | { error: string } {
  const parsed = parsePath(path)
  if (!parsed || parsed.alias !== alias) return { error: 'Invalid path' }
  const hit = valueAt(root, parsed.segments)
  if (!hit.found) return { error: 'Nothing at this path' }
  const last = parsed.segments[parsed.segments.length - 1]
  const name = last === undefined ? alias : typeof last === 'number' ? `[${last}]` : typeof last === 'object' ? `${last.field} ${last.value}` : last
  const depth = Math.min(3, Math.max(0, opts.depth ?? 1))
  return { node: build(hit.value, name, parsed.segments, alias, depth, opts, true) }
}
